import * as knockoutNamespace from 'knockout';
import {ComponentRegistry} from './constants/ComponentRegistry';

const ko = (knockoutNamespace as unknown as {default?: typeof knockoutNamespace}).default ?? knockoutNamespace;

const componentAliasToModuleId: Readonly<Record<string, string>> = {
    'custom-tooltip': 'component/tooltip/tooltip',
    'fa-filter': 'component/fa/filter',
    'fa-icon': 'component/fa/icon',
    'route-destination': 'component/route/destination',
    'error-object': 'component/message/error-object',
    'component-proxy': 'component/component/proxy',
};

const registryModuleIds = new Set<string>([...ComponentRegistry, ...Object.values(componentAliasToModuleId)]);

type ComponentModule = {
    template?: string;
    viewModel?: unknown;
    default?: unknown;
    [key: string]: unknown;
};

/** Production: eager so Vite can inline all components into the single entry chunk per HTML page. */
const componentModules = import.meta.env.PROD
    ? import.meta.glob<ComponentModule>(['./component/**/*.ts', '!./component/**/*.test.ts'], {eager: true})
    : import.meta.glob<ComponentModule>(['./component/**/*.ts', '!./component/**/*.test.ts']);

/** Resolve a preloaded (prod) or dynamically imported (dev) component module. */
function resolveComponentModule(globKey: string): Promise<ComponentModule | undefined> {
    const entry = componentModules[globKey as keyof typeof componentModules];
    if (!entry) {
        return Promise.resolve(undefined);
    }
    if (import.meta.env.PROD) {
        return Promise.resolve(entry as ComponentModule);
    }
    return (entry as () => Promise<ComponentModule>)();
}

function componentNameToGlobKey(moduleId: string): string | undefined {
    if (!moduleId.startsWith('component/')) {
        return undefined;
    }
    const relativePathUnderComponent = moduleId.slice('component/'.length);
    const key = `./component/${relativePathUnderComponent}.ts`;
    return key in componentModules ? key : undefined;
}

function resolveModuleIdForComponentRegistrationName(componentRegistrationName: string): string | undefined {
    const fromAlias = componentAliasToModuleId[componentRegistrationName];
    if (fromAlias) {
        return fromAlias;
    }
    if (registryModuleIds.has(componentRegistrationName)) {
        return componentRegistrationName;
    }
    return undefined;
}

function parseComponentTemplateHtml(templateHtml: string): Node[] {
    const nodes = ko.utils.parseHtmlFragment(templateHtml);
    const arr = Array.isArray(nodes)
        ? (nodes as Node[])
        : Array.from(nodes as unknown as ArrayLike<Node>);
    if ((!arr || arr.length === 0) && templateHtml.trim()) {
        throw new Error(
            'Ko parseHtmlFragment returned no nodes for non-empty markup (check malformed HTML / browser fragment rules).',
        );
    }
    return arr;
}

/** When a module exports a single PascalCase class but not `viewModel` / `default`, pick that constructor. */
function tryResolveSinglePascalCaseViewModel(module: Record<string, unknown>): unknown {
    const skipKeys = new Set(['template', '__esModule']);
    const constructors: unknown[] = [];
    for (const key of Object.keys(module)) {
        if (skipKeys.has(key)) {
            continue;
        }
        const val = module[key];
        if (typeof val === 'function' && /^[A-Z]/.test(key)) {
            constructors.push(val);
        }
    }
    if (constructors.length === 1) {
        return constructors[0];
    }
    return undefined;
}

function resolveViewModelExport(module: Record<string, unknown>): unknown {
    return module['viewModel'] ?? module['default'] ?? tryResolveSinglePascalCaseViewModel(module);
}

/** Native ES6 classes have a non-writable `prototype` (minified output is `class{`, not `class `). */
function isClassConstructor(fn: unknown): fn is new (params: unknown) => unknown {
    if (typeof fn !== 'function') {
        return false;
    }
    const prototypeDescriptor = Object.getOwnPropertyDescriptor(fn, 'prototype');
    return prototypeDescriptor !== undefined && prototypeDescriptor.writable === false;
}

function buildKnockoutDefinitionFromLoadedModule(module: {
    template?: string;
    viewModel?: unknown;
    default?: unknown;
    [key: string]: unknown;
}): {template: Node[]; createViewModel: (params: unknown) => unknown} {
    const templateString = module.template;
    if (typeof templateString !== 'string' || !templateString.trim()) {
        throw new Error('Component module is missing exported non-empty `template` string (check `*.html?raw` import)');
    }
    const viewModelExport = resolveViewModelExport(module);
    if (viewModelExport === undefined) {
        throw new Error(
            'Component module is missing a viewModel: export `viewModel` or `default`, or a single PascalCase class.',
        );
    }
    return {
        template: parseComponentTemplateHtml(templateString),
        createViewModel: (params: unknown) => {
            // Pre-built view model object (no constructor/factory). e.g. `export const viewModel = { label: ko.observable() }`
            if (typeof viewModelExport !== 'function') {
                return viewModelExport;
            }
            // ES6 class export — must use `new`, unless params is already an instance
            // (e.g. `params: tableDef` where tableDef is a TableBasic, or login `params: companySelect`).
            if (isClassConstructor(viewModelExport)) {
                if (params instanceof viewModelExport) {
                    return params;
                }
                return new viewModelExport(params);
            }
            // Factory function — call without `new`. e.g. `component/sync/status-v2/input.ts` (class lives in another file)
            return (viewModelExport as (params: unknown) => unknown)(params);
        },
    };
}

/**
 * Custom elements (<fa-icon>, <route-destination>, …) are only wired to the component
 * binding when `ko.components.isRegistered(tagNameLower)` is true (see knockout
 * `getComponentNameForNode`). That check uses the registry only — it does not ask loaders.
 * These placeholders satisfy `isRegistered`; our unshift ESM loader still wins first in
 * `getConfig` / `loadComponent` with `{ __eadmEsmModuleId }`, so defaults here are unused.
 */
function registerAliasNamesForCustomElements(): void {
    for (const aliasName of Object.keys(componentAliasToModuleId)) {
        if ((ko.components as {isRegistered(name: string): boolean}).isRegistered(aliasName)) {
            continue;
        }
        ko.components.register(aliasName, {
            template: [],
            viewModel(): Record<string, never> {
                return {};
            },
        });
    }
}

export function installEsmComponentLoader(): void {
    registerAliasNamesForCustomElements();

    const knockoutWithComponentLoaders = ko as unknown as {
        components: {loaders: unknown[]};
    };
    knockoutWithComponentLoaders.components.loaders.unshift({
        getConfig(componentRegistrationName: string, callback: (config: unknown) => void) {
            const moduleId = resolveModuleIdForComponentRegistrationName(componentRegistrationName);
            if (!moduleId) {
                callback(null);
                return;
            }
            callback({__eadmEsmModuleId: moduleId});
        },
        loadComponent(
            componentRegistrationName: string,
            componentConfig: {__eadmEsmModuleId?: string},
            callback: (definition: {template: Node[]; createViewModel: (params: unknown) => unknown} | null) => void,
        ) {
            const moduleId = componentConfig.__eadmEsmModuleId;
            if (!moduleId) {
                callback(null);
                return;
            }
            const globKey = componentNameToGlobKey(moduleId);
            if (!globKey) {
                console.error(`eAdm: no glob entry for component module id "${moduleId}"`);
                callback(null);
                return;
            }
            void resolveComponentModule(globKey)
                .then((module) => {
                    if (!module) {
                        console.error(`eAdm: missing component module for "${globKey}"`);
                        callback(null);
                        return;
                    }
                    callback(buildKnockoutDefinitionFromLoadedModule(module));
                })
                .catch((loadError: unknown) => {
                    console.error(`eAdm: failed to load component "${componentRegistrationName}"`, loadError);
                    callback(null);
                });
        },
    } as unknown);

    if (import.meta.hot) {
        for (const globKey of Object.keys(componentModules)) {
            import.meta.hot!.accept(globKey, () => {
                const moduleId = globKey.replace(/^\.\/component\//, 'component/').replace(/\.ts$/, '');
                const koComponents = ko.components as {
                    unregister?(name: string): void;
                    clearCachedDefinition?(name: string): void;
                };
                if (typeof koComponents.unregister === 'function') {
                    koComponents.unregister(moduleId);
                }
                if (typeof koComponents.clearCachedDefinition === 'function') {
                    koComponents.clearCachedDefinition(moduleId);
                }
                location.reload();
            });
        }
        import.meta.hot!.accept(['./main.ts', './appContext.ts', './knockoutBindings.ts'], () => {
            location.reload();
        });
    }
}
